<template>
    <ModalShell title="Add Family Member" subtitle="Add a new family member to your protection plan.">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21M19 8V14M22 11H16M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="#181D27" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </template>
        <!-- Form fields -->
        <div class="form-section">
            <!-- First Name and Last Name row -->
            <div class="form-row">
                <div class="form-field">
                    <label class="field-label">
                        First Name
                        <span class="required">*</span>
                    </label>
                    <PrimeInputText
                        v-model="firstName"
                        placeholder="Jane"
                        :invalid="nameExists"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                    <div v-if="nameExists" class="validation-message">
                        <span>This family member already exists.</span>
                    </div>
                </div>
                <div class="form-field">
                    <label class="field-label">
                        Last Name
                        <span class="required">*</span>
                    </label>
                    <PrimeInputText
                        v-model="lastName"
                        placeholder="Doe"
                        :invalid="nameExists"
                        @keydown.enter="onEnter"
                        @keydown="letterOnlyInputHandler"
                        @paste="letterOnlyInputHandler"
                    />
                </div>
            </div>

            <!-- Email Address -->
            <div class="form-field">
                <label class="field-label">
                    Email Address
                    <span class="required">*</span>
                </label>
                <PrimeInputText
                    v-model="email"
                    placeholder="jane.doe@email.com"
                    :invalid="(email.length > 0 && !isValidEmail(email)) || emailExists"
                    @keydown.enter="onEnter"
                />
                <div v-if="email" class="validation-message">
                    <span v-if="!isValidEmail(email)">Please enter a valid email address.</span>
                    <span v-else-if="emailExists">This family member already exists.</span>
                </div>
            </div>
        </div>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog"
            />
            <PrimeButton
                label="Add Member"
                variant="primary"
                :disabled="!canAddContact"
                :loading="isAddingContact"
                @click="addContact"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import PrimeInputText from 'primevue/inputtext';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useContactsStore } from '~/stores/person/contacts';
import { useVaultStore } from '~/stores/person/vault/vault';
import { ModalNames } from '~/types/modals';

const { letterOnlyInputHandler, isValidEmail } = useUtils();
const { isMobile } = useIsMobile();
const { lockScroll, unlockScroll } = useScrollLock();
const contactsStore = useContactsStore();
const vaultStore = useVaultStore();

const dialogRef = getDialogRef(ModalNames.ADD_CONTACT);
const modalData = computed(() => dialogRef.value?.data || {});

// Form state
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const isAddingContact = ref(false);

const closeDialog = () => {
    resetForm();
    dialogRef.value.close();
};

const resetForm = () => {
    firstName.value = '';
    lastName.value = '';
    email.value = '';
};

// Computed: check if name already exists (case-insensitive)
const nameExists = computed(() => {
    const fullName = `${firstName.value.trim()} ${lastName.value.trim()}`.toLowerCase();
    const contacts = contactsStore.filteredContacts || [];
    return contacts.some(contact => fullName === contact.name?.toLowerCase());
});

// Computed: check if email already exists (case-insensitive)
const emailExists = computed(() => {
    const emailLower = email.value.trim().toLowerCase();
    const contacts = contactsStore.filteredContacts || [];
    return contacts.some(contact => contact.emails?.some(e => e.email?.toLowerCase() === emailLower));
});

// Computed: check if contact already exists (either name or email)
const contactExists = computed(() => nameExists.value || emailExists.value);

// Computed: can add contact only if name and valid email are provided and contact doesn't exist
const canAddContact = computed(() => {
    return firstName.value.trim().length > 0 &&
           lastName.value.trim().length > 0 &&
           email.value.trim().length > 0 &&
           isValidEmail(email.value.trim()) &&
           !contactExists.value;
});

// Watch for modal data changes and prefill relative information
watch(() => modalData.value?.contact, (relativeData) => {
    if (relativeData && typeof relativeData === 'object') {
        firstName.value = relativeData.first_name || '';
        lastName.value = relativeData.last_name || '';
    }
}, { immediate: true });

const onEnter = (e: KeyboardEvent) => {
    e.preventDefault();
    if (canAddContact.value && !isAddingContact.value) {
        addContact();
    }
};

async function addContact() {
    if (isAddingContact.value || contactExists.value) return;

    isAddingContact.value = true;
    const fullName = `${firstName.value.trim()} ${lastName.value.trim()}`;

    const postData = {
        name: fullName,
        emails: [email.value.trim()],
        phone_numbers: [],
        tags: ['family'],
        blocked: false,
        // If license is selected, it would be handled separately via gifting flow
    };

    const success = await contactsStore.createContact(postData);

    if (success) {
        // If this came from an exposure, suppress the original exposure
        if (modalData.value?.fromExposure && modalData.value?.exposureData) {
            try {
                const exposureData = modalData.value.exposureData;
                const exposureType = exposureData.type || 'relative';
                await vaultStore.updateExposure(true, exposureType, exposureData.value);
                await vaultStore.updatePotentialExposures(exposureType, exposureData.value, true);
                await vaultStore.fetchPotentialExposures();
            } catch (error) {
                console.error('Error suppressing exposure:', error);
            }
        }

        isAddingContact.value = false;
        closeDialog();
    } else {
        isAddingContact.value = false;
    }
}

onMounted(() => {
    if (isMobile.value) {
        lockScroll();
    }
});

onUnmounted(() => {
    unlockScroll();
    resetForm();
});
</script>

<style lang="scss" scoped>
.form-section {
    @apply flex flex-col gap-4 w-full;
}

.form-row {
    @apply flex sm:flex-row flex-col gap-4;
}

.form-field {
    @apply flex flex-col gap-1.5 flex-1;
}

.field-label {
    @apply text-sm font-medium text-zinc-700 leading-5;

    .required {
        @apply text-blue-600 ml-0.5;
    }
}

.validation-message {
    @apply flex gap-1 mt-1;

    span {
        @apply text-red-600 font-inter text-sm font-normal leading-5 whitespace-nowrap;
    }
}
</style>
